'use client';

import { type RefObject, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface RailScrollState {
  overflow: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const NO_SCROLL: RailScrollState = {
  overflow: false,
  canScrollLeft: false,
  canScrollRight: false,
};

/**
 * Track a horizontal scroller's overflow + edge positions so prev/next arrows
 * can hide when everything fits and disable at each end. Re-measures on scroll,
 * container resize, and when `itemCount` changes. Shared by every horizontal
 * rail — the My Life clip rail, the CollectionButton/series rail, and the
 * `InfiniteAppendList` horizontal path — so the arrow logic lives in ONE place.
 * 1:1 with RN's `DecoratedHorizontalFlatList` scroll buttons.
 *
 * Lives in `@ps/ui` (not a feature slice) because it depends only on React +
 * lucide and is consumed by the lower-level `InfiniteAppendList` molecule, which
 * must not import from `@ps/features`.
 */
export function useRailScroll(
  ref: RefObject<HTMLElement | null>,
  itemCount: number,
): RailScrollState & { scrollByDirection: (direction: 1 | -1) => void } {
  const [scroll, setScroll] = useState<RailScrollState>(NO_SCROLL);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      setScroll({
        overflow: maxScrollLeft > 1,
        canScrollLeft: el.scrollLeft > 1,
        canScrollRight: el.scrollLeft < maxScrollLeft - 1,
      });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [ref, itemCount]);

  const scrollByDirection = (direction: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    // Page by ~one viewport (RN pages by container width).
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return { ...scroll, scrollByDirection };
}

/**
 * The prev/next chevrons, overlaid on a `relative` rail's edges. Renders nothing
 * when the rail doesn't overflow; each arrow hides at its edge. The parent must
 * be `position: relative`.
 */
export function RailScrollButtons({
  scroll,
  onScroll,
  prevLabel = 'Previous',
  nextLabel = 'Next',
}: {
  scroll: RailScrollState;
  onScroll: (direction: 1 | -1) => void;
  /** Accessible label for the left arrow (e.g. "Previous clips"). */
  prevLabel?: string;
  /** Accessible label for the right arrow (e.g. "Next clips"). */
  nextLabel?: string;
}) {
  if (!scroll.overflow) return null;
  const buttonClass =
    'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-scrim/55 text-white shadow-md transition hover:bg-scrim/70';
  return (
    <>
      {scroll.canScrollLeft ? (
        <button
          type="button"
          onClick={event => {
            // Never let an arrow tap activate the parent card (e.g. the
            // section picker row's select-toggle).
            event.stopPropagation();
            onScroll(-1);
          }}
          aria-label={prevLabel}
          className={`left-1 ${buttonClass}`}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
      {scroll.canScrollRight ? (
        <button
          type="button"
          onClick={event => {
            event.stopPropagation();
            onScroll(1);
          }}
          aria-label={nextLabel}
          className={`right-1 ${buttonClass}`}
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
    </>
  );
}
